
import React, { useEffect, useState } from "react";
import { Col, Row, Spinner, Table, Badge, Container, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import { FaArrowLeft } from "react-icons/fa";

const IRMDView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [reportData, setReportData] = useState({
        basicInfo: {},
        supplierInfo: {},
        locationInfo: {},
        items: []
    });

    useEffect(() => {
        getInspectionReport();
    }, [id]);

    const getInspectionReport = async () => {
        try {
            setIsLoading(true);
            const { data } = await axios.get(`${BASE_URL}inspectionReport/findById/${id}`, {
                headers: authHeader(),
            });

            const formattedData = {
                basicInfo: {
                    inspectionreportno: data.inspectionreportno,
                    jobnumber: data.jobnumber,
                    inspectionreportdate: data.inspectionreportdate,
                    status: data.status,
                    deliverynoteno: data.deliverynoteno,
                    deliverynotedate: data.deliverynotedate,
                    pono: data.pono,
                    podate: data.podate
                },
                supplierInfo: {
                    supplier: data.supplier?.companyname,
                    gstno: data.supplier?.gstno,
                    contactperson: data.contactperson,
                    contactno: data.contactno,
                    email: data.email,
                    address: data.supplierMultipleAddress?.supplieraddress
                },
                locationInfo: {
                    branch: data.branch?.branchname,
                    warehouse: data.warehouse?.warehousename,
                    shipto: data.shipto
                },
                items: data.inspectionReportItems?.map(item => ({
                    product: item.product?.description,
                    partno: item.partno,
                    hsnsacno: item.hsnsacno,
                    serialnumber: item.serialnumber,
                    detailmaterialdescription: item.detailmaterialdescription,
                    make: item.make,
                    mtrno: item.mtrno,
                    visualinspection: item.visualinspection,
                    dimensionalinspection: item.dimensionalinspection,
                    markingonmaterial: item.markingonmaterial,
                    verificationofmtr: item.verificationofmtr,
                    remarksifany: item.remarksifany,
                    quantityreceived: item.quantityreceived,
                    quantityaccepted: item.quantityaccepted,
                    uom: item.uom,
                    heatnobatchno: item.heatnobatchno,
                    platenosrno: item.platenosrno,
                    calibrationvalidupto: item.calibrationvalidupto,
                    productSpecs: {
                        moc: item.moc,
                        type: item.type,
                        size: item.size,
                        rating: item.rating,
                        classes: item.classes,
                        schedule: item.schedule,
                        certification: item.certification
                    }
                })) || []
            };

            setReportData(formattedData);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch inspection report details.'
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'Approved': 'success',
            'Rejected': 'danger',
            'Pending': 'warning',
            'Completed': 'primary',
            'Draft': 'secondary',
            'Submitted': 'info',
            'Active': 'success'
        };
        return statusMap[status] || 'secondary';
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">INSPECTION REPORT - VIEW</h5>
                        <div>
                            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                                <FaArrowLeft className="me-1" />
                                Back
                            </Button>
                        </div>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Report No</small>
                                <div className="fw-bold">{reportData.basicInfo.inspectionreportno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Report Date</small>
                                <div className="fw-bold">{formatDate(reportData.basicInfo.inspectionreportdate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Job Number</small>
                                <div className="fw-bold">{reportData.basicInfo.jobnumber || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge bg={getStatusBadge(reportData.basicInfo.status)} className="px-2 py-1">
                                        {reportData.basicInfo.status || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Basic Information */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Basic Information</h6>
                </div>
                <div className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <small className="text-muted">Delivery Note No</small>
                            <div className="fw-bold">{reportData.basicInfo.deliverynoteno || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">Delivery Note Date</small>
                            <div className="fw-bold">{formatDate(reportData.basicInfo.deliverynotedate)}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">PO No</small>
                            <div className="fw-bold">{reportData.basicInfo.pono || '-'}</div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted">PO Date</small>
                            <div className="fw-bold">{formatDate(reportData.basicInfo.podate)}</div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Supplier and Location Information */}
            <Row className="mb-3">
                <Col md={6}>
                    <div className="shadow-sm border rounded bg-white h-100">
                        <div className="bg-light py-2 px-3 border-bottom">
                            <h6 className="mb-0 fw-bold">Supplier Information</h6>
                        </div>
                        <div className="p-3">
                            <Row className="g-3">
                                <Col md={12}>
                                    <small className="text-muted">Company Name</small>
                                    <div className="fw-bold">{reportData.supplierInfo.supplier || '-'}</div>
                                </Col>
                                <Col md={6}>
                                    <small className="text-muted">Contact Person</small>
                                    <div className="fw-bold">{reportData.supplierInfo.contactperson || '-'}</div>
                                </Col>
                                <Col md={6}>
                                    <small className="text-muted">GST No</small>
                                    <div className="fw-bold">{reportData.supplierInfo.gstno || '-'}</div>
                                </Col>
                                <Col md={6}>
                                    <small className="text-muted">Phone</small>
                                    <div className="fw-bold">{reportData.supplierInfo.contactno || '-'}</div>
                                </Col>
                                <Col md={6}>
                                    <small className="text-muted">Email</small>
                                    <div className="fw-bold">{reportData.supplierInfo.email || '-'}</div>
                                </Col>
                                <Col md={12}>
                                    <small className="text-muted">Address</small>
                                    <div className="fw-bold">{reportData.supplierInfo.address || '-'}</div>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>
                <Col md={6}>
                    <div className="shadow-sm border rounded bg-white h-100">
                        <div className="bg-light py-2 px-3 border-bottom">
                            <h6 className="mb-0 fw-bold">Location Information</h6>
                        </div>
                        <div className="p-3">
                            <Row className="g-3">
                                <Col md={6}>
                                    <small className="text-muted">Branch</small>
                                    <div className="fw-bold">{reportData.locationInfo.branch || '-'}</div>
                                </Col>
                                <Col md={6}>
                                    <small className="text-muted">Warehouse</small>
                                    <div className="fw-bold">{reportData.locationInfo.warehouse || '-'}</div>
                                </Col>
                                <Col md={12}>
                                    <small className="text-muted">Delivery Address</small>
                                    <div className="fw-bold">{reportData.locationInfo.shipto || '-'}</div>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Inspection Items Table - Preserved Original Design */}
            <div className="shadow-sm border rounded bg-white mb-3">
                <div className="bg-light py-2 px-3 border-bottom">
                    <h6 className="mb-0 fw-bold">Inspection Items</h6>
                </div>
                <div className="p-0">
                    <div style={{
                        overflowX: 'auto',
                        borderRadius: '0 0 8px 8px'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: '#fff',
                            margin: 0
                        }}>
                            <thead style={{
                                backgroundColor: '#f8f9fa',
                                borderBottom: '2px solid #dee2e6'
                            }}>
                                <tr>
                                    <th style={viewTableHeaderStyle}>Product</th>
                                    <th style={viewTableHeaderStyle}>Specifications</th>
                                    <th style={viewTableHeaderStyle}>Inspections</th>
                                    <th style={viewTableHeaderStyle}>Quantity</th>
                                    <th style={viewTableHeaderStyle}>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.items.length > 0 ? (
                                    reportData.items.map((item, index) => (
                                        <tr key={index} style={{
                                            borderBottom: '1px solid #e9ecef',
                                            transition: 'background-color 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            {/* Product Column */}
                                            <td style={viewTableCellStyle}>
                                                <div style={{
                                                    fontWeight: '600',
                                                    color: '#2c3e50',
                                                    fontSize: '14px',
                                                    marginBottom: '4px'
                                                }}>
                                                    {item.product || '-'}
                                                </div>
                                                {item.partno && (
                                                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '2px' }}>
                                                        Part No: {item.partno}
                                                    </div>
                                                )}
                                                {item.hsnsacno && (
                                                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                                        HSN/SAC: {item.hsnsacno}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Specifications Column */}
                                            <td style={viewTableCellStyle}>
                                                {item.detailmaterialdescription && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={viewLabelStyle}>Description</div>
                                                        <div style={viewValueStyle}>{item.detailmaterialdescription}</div>
                                                    </div>
                                                )}
                                                {Object.entries(item.productSpecs).map(([key, value]) => (
                                                    value && (
                                                        <div key={key} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            marginBottom: '4px',
                                                            gap: '8px'
                                                        }}>
                                                            <span style={viewSpecLabelStyle}>{key}:</span>
                                                            <span style={viewValueStyle}>{value}</span>
                                                        </div>
                                                    )
                                                ))}
                                            </td>

                                            {/* Inspections Column */}
                                            <td style={viewTableCellStyle}>
                                                <InspectionResult label="Visual" value={item.visualinspection} />
                                                <InspectionResult label="Dimensional" value={item.dimensionalinspection} />
                                                <InspectionResult label="Marking" value={item.markingonmaterial} />
                                                <InspectionResult label="MTR Verify" value={item.verificationofmtr} />
                                                {item.remarksifany && (
                                                    <div style={{ marginTop: '12px' }}>
                                                        <div style={viewLabelStyle}>Remarks</div>
                                                        <div style={viewValueStyle}>{item.remarksifany}</div>
                                                    </div>
                                                )}
                                            </td>

                                            {/* Quantity Column */}
                                            <td style={viewTableCellStyle}>
                                                <div style={{ marginBottom: '12px' }}>
                                                    <div style={viewLabelStyle}>Received</div>
                                                    <div style={{
                                                        ...viewValueStyle,
                                                        fontWeight: '500',
                                                        color: '#495057'
                                                    }}>
                                                        {item.quantityreceived || '0'} {item.uom || ''}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div style={viewLabelStyle}>Accepted</div>
                                                    <div style={{
                                                        ...viewValueStyle,
                                                        fontWeight: '600',
                                                        color: (item.quantityaccepted > 0) ? '#28a745' : '#dc3545'
                                                    }}>
                                                        {item.quantityaccepted || '0'} {item.uom || ''}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Details Column */}
                                            <td style={viewTableCellStyle}>
                                                {item.mtrno && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={viewLabelStyle}>MTR No</div>
                                                        <div style={viewValueStyle}>{item.mtrno}</div>
                                                    </div>
                                                )}
                                                {item.make && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={viewLabelStyle}>Make</div>
                                                        <div style={viewValueStyle}>{item.make}</div>
                                                    </div>
                                                )}
                                                {item.heatnobatchno && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={viewLabelStyle}>Heat/Batch No</div>
                                                        <div style={viewValueStyle}>{item.heatnobatchno}</div>
                                                    </div>
                                                )}
                                                {item.calibrationvalidupto && (
                                                    <div>
                                                        <div style={viewLabelStyle}>Calibration Valid</div>
                                                        <div style={viewValueStyle}>{formatDate(item.calibrationvalidupto)}</div>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{
                                            textAlign: 'center',
                                            padding: '40px 20px',
                                            color: '#6c757d',
                                            fontSize: '14px'
                                        }}>
                                            No inspection items found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                    <FaArrowLeft className="me-1" />
                    Back to List
                </Button>
            </div>
        </Container>
    );
};

// Reusable InspectionResult component
const InspectionResult = ({ label, value }) => {
    if (!value) return null;

    const getBadgeVariant = () => {
        if (value === 'Yes') return 'success';
        if (value === 'No') return 'danger';
        if (value === 'NA') return 'secondary';
        return 'primary';
    };

    return (
        <div className="mb-2">
            <div className="small text-muted">{label}</div>
            <Badge bg={getBadgeVariant()}>{value}</Badge>
        </div>
    );
};

export default IRMDView;

// View Page Table Styles
const viewTableHeaderStyle = {
    padding: '14px 12px',
    textAlign: 'left',
    fontWeight: '600',
    fontSize: '13px',
    color: '#495057',
    borderBottom: '1px solid #dee2e6',
    backgroundColor: '#f8f9fa',
    position: 'sticky',
    top: 0
};

const viewTableCellStyle = {
    padding: '14px 12px',
    verticalAlign: 'top',
    border: '1px solid #e9ecef',
    fontSize: '13px',
    lineHeight: '1.4'
};

const viewLabelStyle = {
    fontSize: '11px',
    fontWeight: '500',
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px'
};

const viewValueStyle = {
    fontSize: '13px',
    color: '#495057',
    lineHeight: '1.3'
};

const viewSpecLabelStyle = {
    fontSize: '12px',
    fontWeight: '500',
    color: '#6c757d',
    minWidth: '60px',
    flexShrink: 0
};